/**
 * Offer compensation — client mirror of `server/offerCore.ts`
 * (owner spec, Oct 18 2026).
 *
 * Every offer carries its own pay: an amount, a currency and a pay
 * cycle. The employer (or an admin issuing on their behalf) MUST
 * state it — the pay on an offer can differ from the job posting's
 * rate, so the form prefills from the posting when it can and always
 * stays editable + required. The server re-validates through
 * `validateOfferCompensation`; nothing here is a security boundary.
 */

export const OFFER_PAY_CYCLES = ['Weekly', 'Bi-Weekly', 'Monthly', 'Yearly', 'Hourly'] as const;
export type OfferPayCycle = (typeof OFFER_PAY_CYCLES)[number];

export interface OfferCompensation {
  amount: number;
  currency: string;
  payCycle: OfferPayCycle;
}

/** Currencies offered in the issuance forms (the server accepts any
 * 3-letter code; this list covers the portal's programs). */
export const OFFER_CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP', 'JMD', 'AUD', 'NZD', 'CHF'];

export const OFFER_COMPENSATION_REQUIRED_ERROR =
  'Please add the pay for this offer — an amount, a currency, and how often it is paid (weekly, bi-weekly, monthly, yearly or hourly). The pay on an offer can be different from the job posting.';

const PAY_CYCLE_PHRASES: Record<OfferPayCycle, string> = {
  Weekly: 'per week',
  'Bi-Weekly': 'every two weeks',
  Monthly: 'per month',
  Yearly: 'per year',
  Hourly: 'per hour',
};

/** Plain-words pay, e.g. "CAD 3,200 per month" / "CAD 20 per hour".
 * Mirrors the server formatter byte-for-byte. */
export function formatOfferCompensation(comp: OfferCompensation | null | undefined): string {
  if (!comp) return '';
  const amount = Number(comp.amount);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  const phrase = PAY_CYCLE_PHRASES[comp.payCycle as OfferPayCycle];
  if (!phrase || !comp.currency) return '';
  const amountText = amount.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${comp.currency} ${amountText} ${phrase}`;
}

export function isValidOfferCompensation(comp: {
  amount: unknown;
  currency: unknown;
  payCycle: unknown;
}): comp is OfferCompensation {
  const amount = typeof comp.amount === 'number' ? comp.amount
    : typeof comp.amount === 'string' && comp.amount.trim() ? Number(comp.amount)
    : NaN;
  if (!Number.isFinite(amount) || amount <= 0) return false;
  if (typeof comp.currency !== 'string' || !/^[A-Z]{3}$/.test(comp.currency.trim().toUpperCase())) return false;
  return typeof comp.payCycle === 'string'
    && (OFFER_PAY_CYCLES as readonly string[]).includes(comp.payCycle);
}

const CURRENCY_SYMBOLS: Record<string, string> = { $: '', '€': 'EUR', '£': 'GBP' };
const CYCLE_PATTERNS: Array<{ cycle: OfferPayCycle; pattern: RegExp }> = [
  { cycle: 'Bi-Weekly', pattern: /bi[-\s]?weekly|every\s+(two|2)\s+weeks|fortnight/i },
  { cycle: 'Hourly', pattern: /hourly|per\s+hour|\/\s*(hr|hour)\b/i },
  { cycle: 'Weekly', pattern: /weekly|per\s+week|\/\s*(wk|week)\b/i },
  { cycle: 'Monthly', pattern: /monthly|per\s+month|\/\s*(mo|month)\b/i },
  { cycle: 'Yearly', pattern: /yearly|annual|per\s+(year|annum)|\/\s*(yr|year)\b/i },
];

/**
 * Best-effort prefill from a job posting's free-text rate ("$20/hr",
 * "CAD 3,200/month", "58,000 per year"). Parses only what is plainly
 * there — an amount, an explicit currency code when present, and a
 * pay cycle when the text names one. Anything it cannot read stays
 * blank for the employer to fill in; the section is required either
 * way, and the employer can always change the prefilled values.
 */
export function parseJobRateText(text: string | null | undefined): {
  amount?: number;
  currency?: string;
  payCycle?: OfferPayCycle;
} {
  const raw = typeof text === 'string' ? text.trim() : '';
  if (!raw) return {};
  const out: { amount?: number; currency?: string; payCycle?: OfferPayCycle } = {};

  const codeMatch = raw.match(/\b(CAD|USD|EUR|GBP|JMD|AUD|NZD|CHF)\b/i);
  if (codeMatch) out.currency = codeMatch[1].toUpperCase();
  else {
    const symbol = Object.keys(CURRENCY_SYMBOLS).find((s) => raw.includes(s));
    if (symbol && CURRENCY_SYMBOLS[symbol]) out.currency = CURRENCY_SYMBOLS[symbol];
  }

  const amountMatch = raw.replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
  if (amountMatch) {
    const amount = Number(amountMatch[1]);
    if (Number.isFinite(amount) && amount > 0) out.amount = amount;
  }

  for (const { cycle, pattern } of CYCLE_PATTERNS) {
    if (pattern.test(raw)) { out.payCycle = cycle; break; }
  }
  return out;
}
